import React from "react";
import { Tabs } from "@/components/ui/tabs";
import { Loader } from "@/components/ui/loader";
import { BusinessReport, TemplateGroup, isAuthoringTemplate, isPrivateTemplate } from "./businessReports.types";
import { BusinessReportsOutcomeList } from "./outcomeList";
import {
  useAddTemplateFavoriteMutation,
  useDeleteTemplateFavoriteMutation,
  useGetBusinessReportsQuery,
  useGetTemplateCategoriesQuery,
  useGetTemplateFavoritesQuery,
  useUpdateBusinessReportMutation,
  useDeleteBusinessReportMutation,
} from "./businessReports.api";
import { BusinessReportsTabs } from "@/modules/snapshots/components/businessReportsTabs";
import { SnapshotCollections } from "@/modules/snapshots/snapshotCollections";
import { useParams } from "react-router";
import { CurrentPdfProvider } from "@/contexts/setPdfContext";
import { ErrorAlert } from "@/components/ui/errorAlert";
import { useBusinessReportAdmin } from "@/hooks/use-business-report-admin";
import { useTemplateLibraryAccess } from "@/hooks/use-template-library-access";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useGroupedTemplates } from "./hooks/useGroupedTemplates";
import { mergeTemplateCategoriesIntoGroups } from "./hooks/templateGroups";
import { getTemplateCategoryCounts, getTemplateWorkspaceSummary } from "./workspaceSummary";
import {
  filterTemplateRows,
  getTemplateCategoryFilterOption,
  getTemplateDisplayCategory,
  getTemplateStatusLabel,
  getTemplateTypeLabel,
  sortTemplateRows,
} from "./templateLibrary";
import type { TemplateCategoryFilterValue, TemplateSort, TemplateSortKey } from "./templateLibrary";
import { CreateBusinessReportModal } from "./modals/createModal";
import { BusinessReportSelectFileModal } from "./modals/selectFileModal";
import { BusinessReportPromptListModal } from "./modals/promptListModal/businessReportPromptListModal";
import { CategoryHeader } from "./components/CategoryHeader";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { ChevronDown, ChevronUp, ClipboardIcon, Search, SlidersHorizontal, Star } from "lucide-react";
import clsx from "clsx";
import { useToggle } from "react-use";
import { toast } from "react-toastify";
import { SnapshotViewsWorkspace } from "@/modules/snapshots/snapshotViews/SnapshotViewsWorkspace";

const emptyList: BusinessReport[] = [];

export const BusinessReportsView = () => {
  const { tab = "collections" } = useParams<{ tab: string }>();
  const { data: businessReports = emptyList, isLoading, error: businessReportsError } = useGetBusinessReportsQuery();
  const { data: favorites = [], error: favoritesError } = useGetTemplateFavoritesQuery();
  const isAdmin = useBusinessReportAdmin();
  const shouldLoadTemplateCategories = tab === "templates" && isAdmin;
  const {
    data: templateCategories = [],
    isLoading: isLoadingTemplateCategories,
    error: templateCategoriesError,
  } = useGetTemplateCategoriesQuery(undefined, { skip: !shouldLoadTemplateCategories });
  const error =
    businessReportsError || favoritesError || (shouldLoadTemplateCategories ? templateCategoriesError : undefined);
  const tableRef = React.useRef<HTMLDivElement>(null);
  const isCollectionsTab = tab === "collections";
  const isSnapshotWorkspaceTab =
    tab === "collections" || tab === "templates" || tab === "recent" || tab === "recent-reports" || tab === "views";

  const favoriteIds = React.useMemo(() => new Set(favorites.map(f => f.business_report_id)), [favorites]);
  const groups = useGroupedTemplates(businessReports, favoriteIds, { includeHidden: true });
  const templateGroups = React.useMemo(
    () => (isAdmin ? mergeTemplateCategoriesIntoGroups(groups, templateCategories) : groups),
    [groups, isAdmin, templateCategories],
  );
  const templateSummary = React.useMemo(() => getTemplateWorkspaceSummary(businessReports), [businessReports]);
  const templateCategoryCounts = React.useMemo(
    () => getTemplateCategoryCounts(businessReports, templateCategories),
    [businessReports, templateCategories],
  );

  if (error) {
    return <ErrorAlert title="Unable to load snapshots" error={error} />;
  }

  return isLoading || (shouldLoadTemplateCategories && isLoadingTemplateCategories) ? (
    <Loader />
  ) : (
    <CurrentPdfProvider>
      <div
        className={isSnapshotWorkspaceTab ? "-mx-4 -my-8 flex-grow flex-col sm:-mx-6 lg:-mx-8" : "flex-grow flex-col"}
      >
        <div
          className={
            isSnapshotWorkspaceTab ? "border-b border-[#ebe5de] bg-[#fffdfb]/80 px-7 backdrop-blur" : "bg-transparent"
          }
        >
          <Tabs value={tab} className="w-full">
            <BusinessReportsTabs selectedTab={tab} />
          </Tabs>
        </div>

        {tab === "views" ? (
          <SnapshotViewsWorkspace />
        ) : isCollectionsTab ? (
          <SnapshotCollections />
        ) : tab === "templates" ? (
          <SnapshotTemplatesWorkspace
            categoryCounts={templateCategoryCounts}
            favoriteIds={favoriteIds}
            groups={templateGroups}
            isAdmin={isAdmin}
            summary={templateSummary}
          />
        ) : tab === "recent" ? (
          <SnapshotOutcomeWorkspace
            emptyLabel="No snapshots found."
            tableRef={tableRef}
            title="Recent Snapshots"
            subtitle="Latest snapshot processing activity across collections."
          />
        ) : tab === "recent-reports" ? (
          <SnapshotOutcomeWorkspace
            emptyLabel="No reports yet - generate one from any business report template."
            tableRef={tableRef}
            templateType="business_report"
            title="Recent Reports"
            subtitle="Generated report outputs from snapshot collections."
          />
        ) : (
          <div className="p-6" />
        )}
      </div>
    </CurrentPdfProvider>
  );
};

interface SnapshotTemplatesWorkspaceProps {
  categoryCounts: Array<{ categoryId: string | null; label: string; value: number }>;
  favoriteIds: ReadonlySet<string>;
  groups: TemplateGroup[];
  isAdmin: boolean;
  summary: ReturnType<typeof getTemplateWorkspaceSummary>;
}

const SnapshotTemplatesWorkspace: React.FC<SnapshotTemplatesWorkspaceProps> = ({
  categoryCounts,
  favoriteIds,
  groups,
  isAdmin,
  summary,
}) => {
  const [query, setQuery] = React.useState("");
  const [hideHiddenTemplates, toggleHideHiddenTemplates] = useToggle(true);
  const [selectedCategoryValue, setSelectedCategoryValue] = React.useState<TemplateCategoryFilterValue>("");
  const [sort, setSort] = React.useState<TemplateSort>({ key: "lastUsed", direction: "desc" });
  const categoryOptions = React.useMemo(() => categoryCounts.map(getTemplateCategoryFilterOption), [categoryCounts]);

  React.useEffect(() => {
    if (selectedCategoryValue && !categoryOptions.some(option => option.value === selectedCategoryValue)) {
      setSelectedCategoryValue("");
    }
  }, [categoryOptions, selectedCategoryValue]);

  const visibleTemplates = React.useMemo(() => {
    const filteredTemplates = filterTemplateRows(
      groups.flatMap(group => group.templates),
      {
        categoryValue: selectedCategoryValue,
        hideHidden: hideHiddenTemplates,
        query,
      },
    );

    return sortTemplateRows(filteredTemplates, sort);
  }, [groups, hideHiddenTemplates, query, selectedCategoryValue, sort]);

  const handleCategorySelect = (categoryValue: TemplateCategoryFilterValue) => {
    setSelectedCategoryValue(current => (current === categoryValue ? "" : categoryValue));
  };

  const handleSort = (key: TemplateSortKey) => {
    setSort(current =>
      current.key === key
        ? { key, direction: current.direction === "asc" ? "desc" : "asc" }
        : { key, direction: key === "created" || key === "lastUsed" ? "desc" : "asc" },
    );
  };

  return (
    <section className="grid min-h-[calc(100vh-4rem)] grid-cols-[350px_minmax(0,1fr)] gap-[34px] overflow-hidden bg-[#f7f3ee] px-8 pb-7 pt-7">
      <aside className="min-w-0 overflow-y-auto border-r border-[#ddd5cb] pr-6" aria-label="Template filters">
        <div className="mb-4 flex gap-2">
          <label className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
            <input
              aria-label="Search templates"
              className="h-10 w-full rounded-md border border-[#ddd5cb] bg-white pl-9 pr-3 text-sm font-medium text-gray-900 outline-none placeholder:text-gray-400 focus:border-desert-sand-700 focus:ring-2 focus:ring-desert-sand-200"
              name="snapshot-template-search"
              onChange={event => setQuery(event.target.value)}
              placeholder="Search templates"
              type="search"
              value={query}
            />
          </label>
          <button
            type="button"
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-[#ddd5cb] bg-white text-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
            aria-label="Advanced template filters unavailable"
            disabled
          >
            <SlidersHorizontal className="h-4 w-4" />
          </button>
        </div>

        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.04em] text-gray-500">Template categories</p>
        <section className="overflow-hidden rounded-lg border border-[#ddd5cb] bg-[#fffdfb]/75 shadow-none">
          <button
            type="button"
            aria-pressed={selectedCategoryValue === ""}
            className={clsx(
              "flex h-12 w-full items-center gap-2 px-4 text-left text-sm font-semibold text-gray-800",
              selectedCategoryValue === "" && "bg-[#f3eadf]",
            )}
            onClick={() => setSelectedCategoryValue("")}
          >
            Templates
            <span className="ml-auto inline-flex h-6 min-w-7 items-center justify-center rounded-full bg-[#eadfd3] px-2 text-xs font-semibold text-gray-700">
              {categoryCounts.reduce((total, category) => total + category.value, 0)}
            </span>
          </button>
          <div className="border-t border-[#ebe5de] px-4 py-2 text-sm text-gray-700">
            {categoryCounts.map(category => {
              const categoryValue = getTemplateCategoryFilterOption(category).value;

              return (
                <CategoryHeader
                  key={category.categoryId ?? category.label}
                  categoryId={category.categoryId}
                  count={category.value}
                  isAdmin={isAdmin}
                  isSelected={selectedCategoryValue === categoryValue}
                  label={category.label}
                  onSelect={() => handleCategorySelect(categoryValue)}
                  variant="sidebarRow"
                />
              );
            })}
          </div>
        </section>
      </aside>

      <main className="min-w-0 overflow-y-auto">
        <SnapshotWorkspaceHeader
          eyebrow="Snapshots / Templates"
          title="Templates"
          subtitle="Template library for snapshot extraction and generated reports."
        />

        <SnapshotWorkspaceMetricStrip
          metrics={[
            { label: "Active templates", value: summary.active, help: "Across all categories" },
            { label: "Snapshot templates", value: summary.snapshotTemplates, help: "Available for collections" },
            { label: "Report templates", value: summary.reportTemplates, help: "Reusable report outputs" },
            { label: "Authoring templates", value: summary.authoringTemplates, help: "Checks for document authoring" },
            { label: "Drafts", value: summary.drafts, help: "Hidden or inactive" },
          ]}
        />

        <section className="overflow-hidden rounded-lg border border-[#ddd5cb] bg-[#fffdfb]/90 shadow-none">
          <header className="flex min-h-16 items-center gap-4 border-b border-[#ebe5de] px-5">
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-[#111b35]">Template library</h2>
              <p className="text-xs font-medium text-gray-500">Compact operational view for scanning templates.</p>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <label className="relative hidden w-64 lg:block">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                <input
                  aria-label="Search template library"
                  className="h-10 w-full rounded-md border border-[#ddd5cb] bg-white pl-9 pr-3 text-sm font-medium text-gray-900 outline-none placeholder:text-gray-400 focus:border-desert-sand-700 focus:ring-2 focus:ring-desert-sand-200"
                  onChange={event => setQuery(event.target.value)}
                  placeholder="Search library"
                  type="search"
                  value={query}
                />
              </label>
              <label className="relative hidden w-52 md:block">
                <span className="sr-only">Filter templates by category</span>
                <select
                  aria-label="Filter templates by category"
                  className="h-10 w-full appearance-none rounded-md border border-[#ddd5cb] bg-white pl-3 pr-9 text-sm font-semibold text-gray-800 outline-none focus:border-desert-sand-700 focus:ring-2 focus:ring-desert-sand-200"
                  onChange={event => setSelectedCategoryValue(event.target.value as TemplateCategoryFilterValue)}
                  value={selectedCategoryValue}
                >
                  <option value="">All categories</option>
                  {categoryOptions.map(category => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
              </label>
              <button
                type="button"
                role="switch"
                aria-checked={hideHiddenTemplates}
                className={clsx(
                  "inline-flex h-10 items-center gap-2 rounded-md border px-3 text-sm font-semibold",
                  hideHiddenTemplates
                    ? "border-[#c8b7a3] bg-[#f3eadf] text-[#111b35]"
                    : "border-[#ddd5cb] bg-white text-gray-700",
                )}
                onClick={toggleHideHiddenTemplates}
              >
                <span
                  aria-hidden
                  className={clsx(
                    "inline-flex h-5 w-9 shrink-0 items-center rounded-full p-0.5 transition-colors",
                    hideHiddenTemplates ? "bg-[#07142d]" : "bg-gray-300",
                  )}
                >
                  <span
                    className={clsx(
                      "h-4 w-4 rounded-full bg-white transition-transform",
                      hideHiddenTemplates && "translate-x-4",
                    )}
                  />
                </span>
                Hide hidden
              </button>
              <button
                type="button"
                className="inline-flex h-10 items-center justify-center rounded-md border border-[#ddd5cb] bg-white px-4 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
                disabled
              >
                Filters
              </button>
              <button
                type="button"
                className="inline-flex h-10 items-center justify-center rounded-md border border-[#ddd5cb] bg-white px-4 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
                disabled
              >
                Columns
              </button>
            </div>
          </header>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[1120px] table-fixed border-collapse">
              <thead>
                <tr className="bg-[#f7f2ec] text-left text-xs font-bold uppercase tracking-[0.04em] text-gray-500">
                  <th className="w-[29%] px-5 py-3">
                    <TemplateSortHeader label="Template" sortKey="template" sort={sort} onSort={handleSort} />
                  </th>
                  <th className="w-[15%] px-4 py-3">
                    <TemplateSortHeader label="Category" sortKey="category" sort={sort} onSort={handleSort} />
                  </th>
                  <th className="w-[12%] px-4 py-3">
                    <TemplateSortHeader label="Type" sortKey="type" sort={sort} onSort={handleSort} />
                  </th>
                  <th className="w-[10%] px-4 py-3">
                    <TemplateSortHeader label="Topics" sortKey="topics" sort={sort} onSort={handleSort} />
                  </th>
                  <th className="w-[13%] px-4 py-3">
                    <TemplateSortHeader label="Created" sortKey="created" sort={sort} onSort={handleSort} />
                  </th>
                  <th className="w-[15%] px-4 py-3">
                    <TemplateSortHeader label="Last used" sortKey="lastUsed" sort={sort} onSort={handleSort} />
                  </th>
                  <th className="w-[10%] px-4 py-3">
                    <TemplateSortHeader label="Status" sortKey="status" sort={sort} onSort={handleSort} />
                  </th>
                  <th className="w-[13%] px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleTemplates.length > 0 ? (
                  visibleTemplates.map(template => (
                    <TemplateLibraryTableRow
                      key={template.id}
                      businessReport={template}
                      isAdmin={isAdmin}
                      isFavorite={favoriteIds.has(template.id)}
                    />
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="px-5 py-8 text-sm text-gray-500">
                      No templates match this view.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </section>
  );
};

interface TemplateSortHeaderProps {
  label: string;
  onSort: (key: TemplateSortKey) => void;
  sort: TemplateSort;
  sortKey: TemplateSortKey;
}

const TemplateSortHeader: React.FC<TemplateSortHeaderProps> = ({ label, onSort, sort, sortKey }) => {
  const isActive = sort.key === sortKey;

  return (
    <button
      type="button"
      className="inline-flex items-center gap-1.5 text-left font-bold uppercase tracking-[0.04em] text-inherit"
      onClick={() => onSort(sortKey)}
      aria-sort={isActive ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}
    >
      {label}
      <span className="flex h-4 flex-col justify-center -space-y-1">
        <ChevronUp
          className={`h-3.5 w-3.5 ${isActive && sort.direction === "asc" ? "text-[#c88b43]" : "text-gray-400"}`}
        />
        <ChevronDown
          className={`h-3.5 w-3.5 ${isActive && sort.direction === "desc" ? "text-[#c88b43]" : "text-gray-400"}`}
        />
      </span>
    </button>
  );
};

interface TemplateLibraryTableRowProps {
  businessReport: BusinessReport;
  isAdmin: boolean;
  isFavorite: boolean;
}

const TemplateLibraryTableRow: React.FC<TemplateLibraryTableRowProps> = ({ businessReport, isAdmin, isFavorite }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [isPromptsModalOpen, setIsPromptsModalOpen] = React.useState(false);
  const [isEditOpen, setIsEditOpen] = React.useState(false);
  const [isDuplicateOpen, setIsDuplicateOpen] = React.useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = React.useState(false);
  const { formatDateShort, formatDayMonth, formatTime } = useDateFormatter();
  const { canManage, canDuplicate } = useTemplateLibraryAccess();
  const [updateBusinessReport, { isLoading: isUpdating }] = useUpdateBusinessReportMutation();
  const [deleteBusinessReport, { isLoading: isDeleting }] = useDeleteBusinessReportMutation();
  const [addFavorite, { isLoading: isAddingFavorite }] = useAddTemplateFavoriteMutation();
  const [removeFavorite, { isLoading: isRemovingFavorite }] = useDeleteTemplateFavoriteMutation();

  const handleFavoriteToggle = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      if (isFavorite) {
        await removeFavorite(businessReport.id).unwrap();
      } else {
        await addFavorite({ business_report_id: businessReport.id }).unwrap();
      }
    } catch {
      toast.error("Failed to update favourite status");
    }
  };

  const handleToggleActive = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    await updateBusinessReport({
      data: { is_active: !businessReport.is_active },
      id: businessReport.id,
    });
  };

  const handleEdit = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsEditOpen(true);
  };

  const handleDuplicate = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDuplicateOpen(true);
  };

  const handleDeleteRequest = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteBusinessReport(businessReport.id).unwrap();
      toast.success("Template deleted.");
      setIsDeleteOpen(false);
    } catch {
      toast.error("Failed to delete the template");
    }
  };

  const manageable = canManage(businessReport);
  const isPrivate = isPrivateTemplate(businessReport);

  const handleTopicsClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsPromptsModalOpen(true);
  };

  const typeLabel = getTemplateTypeLabel(businessReport);
  const statusLabel = getTemplateStatusLabel(businessReport);
  const lastUsedLabel = businessReport.last_used_at ? formatDayMonth(businessReport.last_used_at) : "Never used";
  const lastUsedTime = businessReport.last_used_at ? formatTime(businessReport.last_used_at) : null;

  return (
    <>
      {isEditOpen && (
        <CreateBusinessReportModal businessReport={businessReport} isOpen={isEditOpen} setIsOpen={setIsEditOpen} />
      )}
      {isDuplicateOpen && (
        <CreateBusinessReportModal
          duplicateFrom={businessReport}
          isOpen={isDuplicateOpen}
          setIsOpen={setIsDuplicateOpen}
        />
      )}
      {isDeleteOpen && (
        <Dialog open onOpenChange={open => !open && setIsDeleteOpen(false)}>
          <DialogContent className="max-w-md" onClick={event => event.stopPropagation()}>
            <DialogHeader>
              <DialogTitle>Delete template</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-700">
              Delete &ldquo;{businessReport.name}&rdquo; and its {businessReport.prompts.length} checks? This cannot be
              undone.
            </p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDeleteOpen(false)}>
                Cancel
              </Button>
              <Button type="button" variant="destructive" onClick={handleDeleteConfirm} disabled={isDeleting}>
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {isOpen && <BusinessReportSelectFileModal businessReport={businessReport} onClose={() => setIsOpen(false)} />}
      {isPromptsModalOpen && (
        <BusinessReportPromptListModal businessReport={businessReport} onClose={() => setIsPromptsModalOpen(false)} />
      )}

      <tr
        className="cursor-pointer border-b border-[#ebe5de] text-sm text-gray-700 transition-colors last:border-b-0 hover:bg-[#fbf8f4]"
        // An authoring template has no snapshot to run: opening it shows its checks instead.
        onClick={() => (isAuthoringTemplate(businessReport) ? setIsPromptsModalOpen(true) : setIsOpen(true))}
      >
        <td className="px-5 py-3.5">
          <div className="flex min-w-0 items-center gap-3">
            <span
              className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${
                businessReport.type === "business_report"
                  ? "bg-[#f3e4cf] text-[#b77932]"
                  : businessReport.type === "authoring_template"
                    ? "bg-[#dfe9e4] text-[#2f6b4f]"
                    : "bg-[#07142d] text-white"
              }`}
            >
              <ClipboardIcon className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-[#111b35]">{businessReport.name}</p>
              <p className="truncate text-xs font-medium text-gray-500">
                {businessReport.description ?? "No description"}
              </p>
            </div>
          </div>
        </td>
        <td className="px-4 py-3.5 font-medium text-gray-700">{getTemplateDisplayCategory(businessReport)}</td>
        <td className="px-4 py-3.5">
          <span
            className={`inline-flex h-6 items-center rounded-md px-2.5 text-xs font-semibold ${
              businessReport.type === "business_report"
                ? "bg-[#fff3df] text-[#9b5e15]"
                : businessReport.type === "authoring_template"
                  ? "bg-[#e6f2ec] text-[#2f6b4f]"
                  : "bg-[#eef2f7] text-gray-700"
            }`}
          >
            {typeLabel}
          </span>
          {isPrivate && (
            <span
              className="ml-1.5 inline-flex h-6 items-center rounded-md bg-[#f1ecf7] px-2.5 text-xs font-semibold text-[#5b3f8c]"
              title="Visible to you and library admins only"
            >
              Private
            </span>
          )}
        </td>
        <td className="px-4 py-3.5">
          <button
            type="button"
            className="inline-flex h-7 items-center rounded-full bg-[#f3eadf] px-3 text-xs font-semibold text-[#111b35] hover:bg-[#eadfd3]"
            onClick={handleTopicsClick}
          >
            {businessReport.prompts.length} Topics
          </button>
        </td>
        <td className="px-4 py-3.5">
          <time dateTime={businessReport.created_at} className="font-medium text-gray-700">
            {formatDateShort(businessReport.created_at)}
          </time>
        </td>
        <td className="px-4 py-3.5">
          <span className="font-medium text-gray-700">{lastUsedLabel}</span>
          {lastUsedTime && <div className="text-xs font-medium text-[#b77932]">{lastUsedTime}</div>}
        </td>
        <td className="px-4 py-3.5">
          <span className="inline-flex items-center font-semibold text-gray-700">
            <span
              className={`mr-2 h-2 w-2 rounded-full ${businessReport.is_active ? "bg-emerald-600" : "bg-amber-500"}`}
            />
            {statusLabel}
          </span>
        </td>
        <td className="px-5 py-3.5">
          <div className="flex items-center justify-end gap-1.5">
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:bg-[#f4eadc] hover:text-amber-500 disabled:cursor-not-allowed disabled:opacity-50"
              title={isFavorite ? "Remove from favourites" : "Mark as favourite"}
              onClick={handleFavoriteToggle}
              disabled={isAddingFavorite || isRemovingFavorite}
            >
              <Star className={`h-4 w-4 ${isFavorite ? "fill-amber-400 text-amber-400" : ""}`} />
            </button>
            {canDuplicate(businessReport) && (
              <button
                type="button"
                className="inline-flex h-8 items-center rounded-md border border-[#ddd5cb] bg-white px-3 text-xs font-semibold text-[#344054] hover:bg-[#f7f2ec]"
                onClick={handleDuplicate}
              >
                Duplicate
              </button>
            )}
            {manageable && (
              <button
                type="button"
                className="inline-flex h-8 items-center rounded-md bg-[#07142d] px-3 text-xs font-semibold text-white hover:bg-[#111b35]"
                onClick={handleEdit}
              >
                Edit
              </button>
            )}
            {manageable && !isAdmin && (
              <button
                type="button"
                className="inline-flex h-8 items-center rounded-md bg-[#b4232d] px-3 text-xs font-semibold text-white hover:bg-[#9f1f28] disabled:cursor-not-allowed disabled:opacity-60"
                onClick={handleDeleteRequest}
                disabled={isDeleting}
              >
                Delete
              </button>
            )}
            {isAdmin && (
              <>
                {businessReport.type !== "business_report" && (
                  <button
                    type="button"
                    className={`inline-flex h-8 items-center rounded-md px-3 text-xs font-semibold text-white ${
                      businessReport.is_active ? "bg-[#b4232d] hover:bg-[#9f1f28]" : "bg-gray-600 hover:bg-gray-700"
                    } disabled:cursor-not-allowed disabled:opacity-60`}
                    onClick={handleToggleActive}
                    disabled={isUpdating}
                  >
                    {businessReport.is_active ? "Hide" : "Restore"}
                  </button>
                )}
              </>
            )}
          </div>
        </td>
      </tr>
    </>
  );
};

interface SnapshotOutcomeWorkspaceProps {
  emptyLabel: string;
  subtitle: string;
  tableRef: React.RefObject<HTMLDivElement | null>;
  templateType?: "business_report";
  title: string;
}

const SnapshotOutcomeWorkspace: React.FC<SnapshotOutcomeWorkspaceProps> = ({
  emptyLabel,
  subtitle,
  tableRef,
  templateType,
  title,
}) => (
  <section className="min-h-[calc(100vh-4rem)] overflow-hidden bg-[#f7f3ee] px-8 pb-7 pt-7">
    <SnapshotWorkspaceHeader eyebrow={`Snapshots / ${title}`} title={title} subtitle={subtitle} />
    <BusinessReportsOutcomeList
      emptyLabel={emptyLabel}
      tableRef={tableRef}
      templateType={templateType}
      title={title}
      variant="workspace"
    />
  </section>
);

interface SnapshotWorkspaceHeaderProps {
  eyebrow: string;
  subtitle: string;
  title: string;
}

const SnapshotWorkspaceHeader: React.FC<SnapshotWorkspaceHeaderProps> = ({ eyebrow, subtitle, title }) => (
  <header className="mb-5">
    <p className="mb-2 text-sm font-semibold text-gray-600">{eyebrow}</p>
    <h1 className="truncate text-3xl font-bold leading-tight text-[#111b35]">{title}</h1>
    <p className="mt-1 text-sm font-medium text-gray-500">{subtitle}</p>
  </header>
);

interface SnapshotWorkspaceMetric {
  help: string;
  label: string;
  value: number;
}

interface SnapshotWorkspaceMetricStripProps {
  metrics: SnapshotWorkspaceMetric[];
}

const SnapshotWorkspaceMetricStrip: React.FC<SnapshotWorkspaceMetricStripProps> = ({ metrics }) => (
  <section
    aria-label="Snapshot workspace summary"
    className="mb-5 grid overflow-hidden rounded-lg border border-[#ddd5cb] bg-[#fffdfb]/90 shadow-none md:grid-cols-4"
  >
    {metrics.map(metric => (
      <div
        key={metric.label}
        className="min-w-0 border-b border-[#e7e0d9] px-6 py-5 last:border-b-0 md:border-b-0 md:border-r md:last:border-r-0"
      >
        <p className="truncate text-sm font-semibold text-gray-700">{metric.label}</p>
        <p className="mt-1 text-3xl font-bold leading-none text-[#111b35]">{metric.value}</p>
        <p className="mt-1 truncate text-xs font-medium text-gray-500">{metric.help}</p>
      </div>
    ))}
  </section>
);
